import React, { useEffect, useMemo, useState } from "react";
import { Box, useMediaQuery, CircularProgress, Typography, IconButton } from "@mui/material";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import CustomButton from "../../../../components/CustomButton";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { useTranslation } from "react-i18next";
import maparrow from "../../../../assets/maparrow.svg";
import CloseIcon from "@mui/icons-material/Close";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";
import {
  getGoogleMapsPinIconForStatus,
  getStatusBadgeBackgroundColor,
  resolvePunchPinStatusKey,
} from "../../../../constants/statusPalette";

const MAP_STATUS_LEGEND_ITEMS = [
  { key: "CLOCK_IN", labelKey: "clock_in" },
  { key: "EARLY_CLOCK_IN", labelKey: "early_clock_in" },
  { key: "LATE_ARRIVAL", labelKey: "late_clock_in" },
  { key: "BREAK", labelKey: "break_start" },
  { key: "BREAK_END", labelKey: "break_end" },
  { key: "CLOCK_OUT", labelKey: "clock_out" },
  { key: "EARLY_CLOCK_OUT", labelKey: "early_clock_out" },
  { key: "LATE_CLOCK_OUT", labelKey: "late_clock_out" },
  { key: "ON_LEAVE", labelKey: "on_leave" },
  { key: "ON_HALF_LEAVE", labelKey: "on_half_leave" },
  { key: "ABSENT", labelKey: "absent" },
];

const LEGEND_LABEL_KEY_BY_STATUS = Object.fromEntries(
  MAP_STATUS_LEGEND_ITEMS.map((item) => [item.key, item.labelKey])
);

const STATUS_LABEL_KEY_ALIASES = {
  ON_TIME_CLOCK_IN: "CLOCK_IN",
  LATE_CLOCK_IN: "LATE_ARRIVAL",
  BREAK_START: "BREAK",
  ON_BREAK: "BREAK",
  ON_TIME_CLOCK_OUT: "CLOCK_OUT",
  PRESENT: "CLOCK_IN",
  EARLY_OUT: "EARLY_CLOCK_OUT",
  LATE_AND_EARLY_OUT: "LATE_CLOCK_OUT",
  HALF_DAY: "ON_HALF_LEAVE",
  HALF_LEAVE: "ON_HALF_LEAVE",
  LEAVE: "ON_LEAVE",
  HAS_ISSUES: "HAS_ISSUES",
};

const normalizeMapStatusKey = (status) =>
  String(status || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");

const getMapStatusLabelKey = (status) => {
  const normalized = normalizeMapStatusKey(status);
  if (!normalized || normalized === "-") return null;

  if (normalized === "HAS_ISSUES") {
    return "attendanceStatus.unvalidatedPunches";
  }

  const legendKey = STATUS_LABEL_KEY_ALIASES[normalized] || normalized;
  return LEGEND_LABEL_KEY_BY_STATUS[legendKey] || null;
};

const leafletIconCache = new Map();

const getLeafletPinIcon = (status) => {
  const key = String(status || "default");
  if (!leafletIconCache.has(key)) {
    const pinIcon = getGoogleMapsPinIconForStatus(status);
    leafletIconCache.set(
      key,
      L.icon({
        iconUrl: pinIcon.url,
        iconSize: [30, 42],
        iconAnchor: [15, 41],
        popupAnchor: [0, -36],
      })
    );
  }
  return leafletIconCache.get(key);
};

const FitBounds = ({ markers }) => {
  const map = useMap();

  useEffect(() => {
    if (map && markers.length > 0) {
      const validMarkers = markers.filter(
        (m) => typeof m.lat === "number" && typeof m.lng === "number"
      );
      if (validMarkers.length === 0) return;

      const bounds = L.latLngBounds(validMarkers.map((m) => [m.lat, m.lng]));

      setTimeout(() => {
        try {
          map.fitBounds(bounds, { padding: [50, 50] });
        } catch (err) {
          console.error("fitBounds error:", err);
        }
      }, 100);
    }
  }, [markers, map]);

  return null;
};

const MapMarkers = ({ markers, getStatusLabel }) =>
  markers.map((m) => (
    <Marker key={m.id} position={[m.lat, m.lng]} icon={getLeafletPinIcon(m.status)}>
      <Popup>
        <b>{m.worker}</b>
        <br />
        {m.time}
        <br />
        {m.locationText}
        <br />
        Status: {getStatusLabel(m.status)}
      </Popup>
    </Marker>
  ));

const AttendanceMap = ({ workerId }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:768px)");
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFullMapOpen, setIsFullMapOpen] = useState(false);

  const formatLocalTime = (utcString) => {
    const localValue = convertUTCToLocalDateTime(utcString, {
      locale: "en-GB",
      formatOptions: { hour: "numeric", minute: "2-digit", hour12: true },
    });
    return localValue === "—" ? "--" : localValue;
  };

  const toDisplayTime = (value) => {
    if (value === null || value === undefined || value === "") return "--";

    const converted = formatLocalTime(value);
    if (converted !== "--") return converted;

    if (typeof value === "string" && value.trim()) return value.trim();
    return "--";
  };

  const getDisplayPunchTime = (punch) =>
    toDisplayTime(punch?.occurredAt || punch?.formattedDateTime || punch?.time || null);

  const getStatusLabel = (status) => {
    const labelKey = getMapStatusLabelKey(status);
    return labelKey ? t(labelKey) : status || "-";
  };

  const mapStatusLegend = useMemo(
    () =>
      MAP_STATUS_LEGEND_ITEMS.map((item) => ({
        key: item.key,
        label: t(item.labelKey),
      })),
    [t]
  );

  useEffect(() => {
    if (!workerId || !decryptedToken) return;

    setLoading(true);
    fetch(`${BASE_URL}/company-admins/attendance/history?pageSize=50&workerId=${workerId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${decryptedToken}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.error && data.data?.records) {
          const points = [];
          data.data.records.forEach((rec) => {
            rec.punches?.forEach((p) => {
              if (p.location?.gpsLatitude && p.location?.gpsLongitude) {
                const lat = parseFloat(p.location.gpsLatitude);
                const lng = parseFloat(p.location.gpsLongitude);

                if (!isNaN(lat) && !isNaN(lng)) {
                  const pinStatus = resolvePunchPinStatusKey(p, rec.statusBadge?.text);
                  points.push({
                    id: p.id,
                    lat,
                    lng,
                    worker: rec.worker?.fullName || "Unknown Worker",
                    time: getDisplayPunchTime(p),
                    locationText: p.location?.locationText || "Unknown Location",
                    status: pinStatus,
                  });
                }
              }
            });
          });
          setMarkers(points);
        } else {
          setMarkers([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching attendance map data:", err);
        setMarkers([]);
      })
      .finally(() => setLoading(false));
  }, [decryptedToken, workerId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 350 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (markers.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 5 }}>
        <Typography>{t("attendanceSettings.noWorkers")}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          px: 2,
          py: 1.5,
          backgroundColor: "#fff",
          borderBottom: "1px solid #091E4224",
        }}
      >
        {mapStatusLegend.map((item) => (
          <Box
            key={item.key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.8,
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: getStatusBadgeBackgroundColor(item.key),
              }}
            />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "#44546F",
                fontFamily: "Poppins, sans-serif",
                whiteSpace: "nowrap",
              }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ position: "relative", width: "100%", height: isMobile ? 300 : 400 }}>
        <MapContainer
          center={[33.6844, 73.0479]}
          zoom={5}
          style={{ width: "100%", height: "100%", borderRadius: 8 }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />
          <FitBounds markers={markers} />
          <MapMarkers markers={markers} getStatusLabel={getStatusLabel} />
        </MapContainer>

        <CustomButton
          onClick={() => setIsFullMapOpen(true)}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            backgroundColor: "#006EC2",
            color: "white",
            fontSize: "12px",
            padding: "4px 10px",
            borderRadius: "6px",
            zIndex: 1000,
          }}
          label={
            <>
              {t("dashboard.viewFullMap")}
              <Box
                component="img"
                src={maparrow}
                alt="view icon"
                sx={{ ml: 1, width: 15, height: 13, objectFit: "contain" }}
              />
            </>
          }
        />

        {isFullMapOpen && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              zIndex: 2000,
              backgroundColor: "white",
            }}
          >
            <IconButton
              onClick={() => setIsFullMapOpen(false)}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 2100,
                backgroundColor: "white",
                "&:hover": { backgroundColor: "white" },
              }}
            >
              <CloseIcon sx={{ color: "red" }} />
            </IconButton>

            <MapContainer
              center={[33.6844, 73.0479]}
              zoom={5}
              style={{ width: "100%", height: "100%" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />
              <FitBounds markers={markers} />
              <MapMarkers markers={markers} getStatusLabel={getStatusLabel} />
            </MapContainer>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AttendanceMap;
